import {Box2, Matrix3x2, Matrix3x2Like, Size2Like, Vector2, Vector2Like} from "@cm/math"
import {Descriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {EvaluatedParameters, GetParameters} from "@cm/graph-v2-final"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/affine-transform/definition"

type Definition = typeof definition

export const determineDescriptor = (
    parameters: EvaluatedParameters<GetParameters<Definition>>,
): {
    descriptor: Descriptor
    resultOffset: Vector2Like
} => {
    const resultBox = parameters.resultSize ? Box2.fromSize(parameters.resultSize) : computeResultSize(parameters.sourceImage.descriptor, parameters.transform)
    const resultOffset = parameters.options.compensateOffset
        ? {
              x: resultBox.x,
              y: resultBox.y,
          }
        : {
              x: 0,
              y: 0,
          }
    const descriptor: Descriptor = {
        width: resultBox.width,
        height: resultBox.height,
        channels: parameters.sourceImage.descriptor.channels,
    }
    return {
        descriptor,
        resultOffset,
    }
}

export const computeResultSize = (sourceSize: Size2Like, transform: Matrix3x2Like): Box2 => {
    const matrix = Matrix3x2.fromMatrix3x2Like(transform)
    const p00 = new Vector2(0, 0)
    const p10 = new Vector2(sourceSize.width, 0)
    const p01 = new Vector2(0, sourceSize.height)
    const p11 = new Vector2(sourceSize.width, sourceSize.height)
    const p00t = matrix.multiplyVector(p00)
    const p10t = matrix.multiplyVector(p10)
    const p01t = matrix.multiplyVector(p01)
    const p11t = matrix.multiplyVector(p11)
    const minX = Math.min(p00t.x, p10t.x, p01t.x, p11t.x)
    const maxX = Math.max(p00t.x, p10t.x, p01t.x, p11t.x)
    const minY = Math.min(p00t.y, p10t.y, p01t.y, p11t.y)
    const maxY = Math.max(p00t.y, p10t.y, p01t.y, p11t.y)
    return new Box2(Math.round(minX), Math.round(minY), Math.round(maxX - minX), Math.round(maxY - minY))
}
